/**
 * Schedule-level edit actions (cancel line, edit quantity) for the GRN
 * Allocation Repair page.
 *
 * Extracted from ``GrnAllocationRepair.tsx`` so that page stays under the
 * project's 500-line ceiling. Owns the modal-target state + the API calls and
 * re-fetches the detail/list on success.
 *
 * @param poLineId - the currently-selected PO line id (null when none open)
 * @param refresh - callback to re-fetch the open PO line's detail + the list
 * @returns the cancel/qty modal targets, in-flight flags, and their handlers
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { grnRepairAPI, type RepairSchedule } from '../../lib/api/grn-repair';

export function useGrnScheduleEdits(poLineId: number | null, refresh: () => void) {
  const [cancelTarget, setCancelTarget] = useState<RepairSchedule | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [uncancelTarget, setUncancelTarget] = useState<RepairSchedule | null>(null);
  const [uncancelling, setUncancelling] = useState(false);
  const [qtyTarget, setQtyTarget] = useState<RepairSchedule | null>(null);
  const [savingQty, setSavingQty] = useState(false);

  const performCancelSchedule = useCallback(async (schedules: RepairSchedule[]) => {
    if (poLineId === null || !cancelTarget) return;
    setCancelling(true);
    try {
      const resp = await grnRepairAPI.cancelSchedule(cancelTarget.id);
      const tgtId = resp.data.target_schedule_id;
      const tgtLine = tgtId ? schedules.find((s) => s.id === tgtId)?.schedule_line_number : null;
      const where = tgtId ? `→ line ${tgtLine ?? tgtId}` : '(no target — left unallocated)';
      toast.success(`Line ${cancelTarget.schedule_line_number} cancelled. ${resp.data.transferred} alloc(s) ${where}.`);
      setCancelTarget(null);
      refresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  }, [poLineId, cancelTarget, refresh]);

  const performUncancelSchedule = useCallback(async () => {
    if (poLineId === null || !uncancelTarget) return;
    setUncancelling(true);
    try {
      await grnRepairAPI.uncancelSchedule(uncancelTarget.id);
      toast.success(`Line ${uncancelTarget.schedule_line_number} reactivated.`);
      setUncancelTarget(null);
      refresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Reactivate failed');
    } finally {
      setUncancelling(false);
    }
  }, [poLineId, uncancelTarget, refresh]);

  const performSetQty = useCallback(async (qty: string) => {
    if (poLineId === null || !qtyTarget) return;
    setSavingQty(true);
    try {
      const resp = await grnRepairAPI.setScheduleQty(qtyTarget.id, qty.trim());
      toast.success(`Line ${qtyTarget.schedule_line_number} qty → ${resp.data.schedule_qty} (${resp.data.status}).`);
      setQtyTarget(null);
      refresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Qty update failed');
    } finally {
      setSavingQty(false);
    }
  }, [poLineId, qtyTarget, refresh]);

  return {
    cancelTarget, setCancelTarget, cancelling, performCancelSchedule,
    uncancelTarget, setUncancelTarget, uncancelling, performUncancelSchedule,
    qtyTarget, setQtyTarget, savingQty, performSetQty,
  };
}
